import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import {
  tag_colorTagDefaultBackground,
  tag_colorTagDefaultText,
  tag_colorTagPendingBackground,
  tag_colorTagPendingText,
} from 'ext-ui/style/colors'
import { cornerRadius, spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_sm,
} from 'ext-ui/style/typography'
import React from 'react'
import ReceiptReconciledCheckIcon from 'src/assets/svgs/ReceiptAgentReconciledCheckIcon.svg'
import ReceiptRecoveryNeedsReviewIcon from 'src/assets/svgs/ReceiptRecoveryNeedsReviewIcon.svg'
import ReceiptRecoveryWaitingOnSpenderIcon from 'src/assets/svgs/ReceiptRecoveryWaitingOnSpenderIcon.svg'
import PolicyStatusTooltipContent from 'src/components/TransactionsDataGrid/cells/PolicyStatusTooltipContent'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptRecoveryLifecycleState } from 'src/models/transaction.models'
import styled from 'styled-components'

interface ReceiptAgentStatusCellProps {
  status?: ReceiptRecoveryLifecycleState
  variant?: 'pill' | 'icon'
}

type ReceiptAgentStatusPillColor = 'default' | 'pending'

const receiptAgentStatusPillColors: Record<
  ReceiptAgentStatusPillColor,
  { background: string; text: string }
> = {
  default: {
    background: tag_colorTagDefaultBackground,
    text: tag_colorTagDefaultText,
  },
  pending: {
    background: tag_colorTagPendingBackground,
    text: tag_colorTagPendingText,
  },
}

const ReceiptAgentStatusPill = styled.span.attrs({
  role: 'status',
})<{
  $color: ReceiptAgentStatusPillColor
}>`
  display: flex;
  height: ${spacing(5)};
  padding: 0 ${spacing(2)};
  justify-content: center;
  align-items: center;
  gap: ${spacing(1)};
  border-radius: ${cornerRadius};
  background: ${({ $color }) =>
    receiptAgentStatusPillColors[$color].background};
  color: ${({ $color }) => receiptAgentStatusPillColors[$color].text};
  ${font_sm};
  font-family: ${font_family_weight_medium};
`

const ReceiptReconciledStatus = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing(1)};
`

const ReceiptReconciledLabel = styled.p`
  margin: 0;
  color: #697080;
  ${font_sm};
  font-family: ${font_family_weight_regular};
  white-space: nowrap;
`

export default function ReceiptAgentStatusCell({
  status,
  variant = 'pill',
}: ReceiptAgentStatusCellProps): React.ReactElement | null {
  const translate = useTranslate()

  const presentation = (() => {
    switch (status) {
      case ReceiptRecoveryLifecycleState.NeedsReview:
        return {
          icon: ReceiptRecoveryNeedsReviewIcon,
          iconTestId: 'receipt-agent-needs-review-icon',
          label: translate('Needs Review'),
          tooltip: translate('Receipt needs review'),
          color: 'pending' as const,
        }
      case ReceiptRecoveryLifecycleState.WaitingOnSpender:
        return {
          icon: ReceiptRecoveryWaitingOnSpenderIcon,
          iconTestId: 'receipt-agent-waiting-on-spender-icon',
          label: translate('Waiting on Spender'),
          tooltip: translate('Waiting on Spender'),
          color: 'default' as const,
        }
      case ReceiptRecoveryLifecycleState.ReceiptReconciled:
        return {
          icon: ReceiptReconciledCheckIcon,
          iconTestId: 'receipt-agent-reconciled-check',
          label: translate('Receipt Reconciled'),
          tooltip: translate('Receipt Reconciled'),
          color: 'default' as const,
        }
      default:
        return undefined
    }
  })()

  if (presentation === undefined) return null

  const icon = (
    <img
      alt=""
      data-testid={presentation.iconTestId}
      src={presentation.icon}
      width={16}
      height={16}
    />
  )

  if (variant === 'icon') {
    return (
      <TooltipIcon
        placement="top"
        icon={
          <span aria-label={presentation.tooltip} role="img">
            {icon}
          </span>
        }
        iconTestId={`receipt-agent-${status?.toLowerCase()}-tooltip`}
      >
        <PolicyStatusTooltipContent>
          {presentation.tooltip}
        </PolicyStatusTooltipContent>
      </TooltipIcon>
    )
  }

  switch (status) {
    case ReceiptRecoveryLifecycleState.NeedsReview:
      return (
        <ReceiptAgentStatusPill $color="pending">
          {icon}
          {presentation.label}
        </ReceiptAgentStatusPill>
      )
    case ReceiptRecoveryLifecycleState.WaitingOnSpender:
      return (
        <ReceiptAgentStatusPill $color="default">
          {icon}
          {presentation.label}
        </ReceiptAgentStatusPill>
      )
    case ReceiptRecoveryLifecycleState.ReceiptReconciled:
      return (
        <ReceiptReconciledStatus data-testid="receipt-agent-reconciled-status">
          {icon}
          <ReceiptReconciledLabel>{presentation.label}</ReceiptReconciledLabel>
        </ReceiptReconciledStatus>
      )
    default:
      return null
  }
}
